import React, { useEffect, useState, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchLeaderboard } from "../../functions/fetchLeaderboard";
import { resolveActiveLobbyName } from "../../lib/scribbleScoring";
import { setBackButtonUrl } from "../uiSlice";
import "./leaderboard.css";

function rowUserId(row) {
  return String(row?.userId ?? row?.userid ?? row?.id ?? "");
}

function LeaderboardRow({ rank, name, points, isMe, featured = false, compact = false }) {
  const pts = Number(points) || 0;
  const displayName = String(name || "Player").toUpperCase();

  return (
    <div
      className={`lb-row${featured ? " lb-row--featured" : ""}${compact ? " lb-row--compact" : ""}${isMe ? " lb-row--me" : ""}`}
    >
      <span className="lb-rank-badge" aria-label={`Rank ${rank}`}>
        {rank}
      </span>
      <div className="lb-row-body">
        <span className="lb-name" title={displayName}>
          {displayName}
          {isMe ? <span className="lb-you-tag">YOU</span> : null}
        </span>
        <span className="lb-score">{pts}</span>
      </div>
    </div>
  );
}

function RankSlot({ entry, isMe, featured = false, cellClass = "" }) {
  const cellCls = ["lb-slot", "lb-cell", cellClass].filter(Boolean).join(" ");
  if (!entry) {
    return <div className={`${cellCls} lb-slot--empty`.trim()} aria-hidden="true" />;
  }
  return (
    <div className={cellCls}>
      <LeaderboardRow
        rank={entry.rank}
        name={entry.name}
        points={entry.points}
        isMe={isMe(entry.userId)}
        featured={featured}
        compact={!featured}
      />
    </div>
  );
}

export default function Leaderboard() {
  const location = useLocation();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);

  const [leaders, setLeaders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    dispatch(setBackButtonUrl("/leaderboard"));
  }, [dispatch]);

  useEffect(() => {
    const push = () => {
      try {
        window.history.pushState(null, "", window.location.href);
      } catch {
        /* ignore */
      }
    };
    push();
    const onPopState = (e) => {
      e?.preventDefault?.();
      push();
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const storedUserData = useMemo(() => {
    try {
      const raw = sessionStorage.getItem("userData") || "{}";
      return JSON.parse(raw) || {};
    } catch {
      return {};
    }
  }, []);

  const currentUserId = useMemo(
    () =>
      user?.userId ??
      user?.userid ??
      user?.id ??
      storedUserData.userId ??
      storedUserData.userid ??
      storedUserData.id,
    [user, storedUserData]
  );

  const sessionId = location.state?.sessionId || storedUserData.sessionId;
  const organizationId = location.state?.organizationId || storedUserData.organizationId;
  const token = sessionId && organizationId ? `${sessionId}&${organizationId}` : "";

  const lobbyName = useMemo(
    () => resolveActiveLobbyName(location.state?.lobby_name),
    [location.state?.lobby_name]
  );

  useEffect(() => {
    if (!token || !sessionId || !organizationId) {
      setError("Missing session data");
      setLoading(false);
      return;
    }
    if (!lobbyName) {
      setError("Missing lobby. Cannot load leaderboard for this game.");
      setLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const data = await fetchLeaderboard({
          token,
          sessionId,
          organizationId,
          lobby_name: lobbyName,
        });
        if (cancelled) return;
        const lobbyKey = lobbyName.trim();
        const sorted = Array.isArray(data)
          ? [...data]
              .filter((row) => String(row.lobby_name ?? "").trim() === lobbyKey)
              .sort((a, b) => (Number(b.total_score) || 0) - (Number(a.total_score) || 0))
          : [];
        setLeaders(sorted);
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load leaderboard");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token, sessionId, organizationId, lobbyName]);

  const ranked = useMemo(
    () =>
      leaders.map((row, index) => ({
        rank: index + 1,
        userId: rowUserId(row),
        name: row.name || rowUserId(row) || "Player",
        points: Number(row.total_score) || 0,
      })),
    [leaders]
  );

  const top5 = useMemo(() => ranked.slice(0, 5), [ranked]);

  const currentUserEntry = useMemo(() => {
    if (!currentUserId) return null;
    return ranked.find((r) => String(r.userId) === String(currentUserId)) || null;
  }, [ranked, currentUserId]);

  const isMe = (userId) => currentUserId && String(userId) === String(currentUserId);

  const byRank = useMemo(() => {
    const map = new Map();
    top5.forEach((e) => map.set(e.rank, e));
    return map;
  }, [top5]);

  const showMeOutsideTop5 = Boolean(currentUserEntry && currentUserEntry.rank > 5);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    html.classList.add("leaderboard-static-bg");
    body.classList.add("leaderboard-static-bg");
    /* Let leaderboard.css media queries control bg (not desktop inline from other pages). */
    html.style.backgroundImage = "";
    body.style.backgroundImage = "";
    return () => {
      html.classList.remove("leaderboard-static-bg");
      body.classList.remove("leaderboard-static-bg");
      html.style.backgroundImage = "";
      body.style.backgroundImage = "";
    };
  }, []);

  return (
    <div className="leaderboard-page">
      <div className="lb-deco lb-deco--tri" aria-hidden="true" />
      <div className="lb-deco lb-deco--circle" aria-hidden="true" />

      {loading ? (
        <div className="leaderboard-loading">Loading leaderboard…</div>
      ) : error ? (
        <div className="leaderboard-error">{error}</div>
      ) : ranked.length === 0 ? (
        <div className="leaderboard-empty">No leaderboard data available</div>
      ) : (
        <div className="lb-wrap">
          <header className="lb-header">
            <h1 className="lb-title-pill">LEADERBOARD</h1>
          </header>

          <div className="lb-card">
            {/* Desktop: rows align 1|3, 2|4, 5|you (left / right) */}
            <div className="lb-split-view">
              <div className="lb-grid">
                <div className="lb-grid-row">
                  <RankSlot entry={byRank.get(1)} isMe={isMe} featured cellClass="lb-cell--left" />
                  <RankSlot entry={byRank.get(3)} isMe={isMe} cellClass="lb-cell--right" />
                </div>
                <div className="lb-grid-row">
                  <RankSlot entry={byRank.get(2)} isMe={isMe} cellClass="lb-cell--left" />
                  <RankSlot entry={byRank.get(4)} isMe={isMe} cellClass="lb-cell--right" />
                </div>
                <div className="lb-grid-row">
                  <RankSlot entry={byRank.get(5)} isMe={isMe} cellClass="lb-cell--left" />
                  {showMeOutsideTop5 && currentUserEntry ? (
                    <div className="lb-slot lb-cell lb-cell--right lb-slot--me">
                      <LeaderboardRow
                        rank={currentUserEntry.rank}
                        name={currentUserEntry.name}
                        points={currentUserEntry.points}
                        isMe
                        compact
                      />
                    </div>
                  ) : (
                    <div className="lb-slot lb-cell lb-cell--right lb-slot--empty" aria-hidden="true" />
                  )}
                </div>
              </div>
            </div>

            {/* Mobile: single-column stack (easier to read on narrow screens) */}
            <div className="lb-stack-view">
              <ul className="lb-stack-list">
                {top5.map((entry) => (
                  <li key={entry.userId || entry.rank}>
                    <LeaderboardRow
                      rank={entry.rank}
                      name={entry.name}
                      points={entry.points}
                      isMe={isMe(entry.userId)}
                      featured={entry.rank === 1}
                    />
                  </li>
                ))}
              </ul>
            </div>

            {showMeOutsideTop5 && currentUserEntry && (
              <div className="lb-me-below">
                <p className="lb-me-label">Your rank</p>
                <LeaderboardRow
                  rank={currentUserEntry.rank}
                  name={currentUserEntry.name}
                  points={currentUserEntry.points}
                  isMe
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
